/**
 * Global reveal-on-scroll for [data-reveal] elements.
 * Uses one shared IntersectionObserver + GSAP for the actual tween.
 *
 * Usage: add `data-reveal` to any element. Optionally add `data-reveal="left"` etc.
 * Supported directions: "up" (default), "down", "left", "right", "none".
 */
import { gsap } from 'gsap'

type RevealDir = 'up' | 'down' | 'left' | 'right' | 'none'

const DEFAULTS: Record<RevealDir, gsap.TweenVars> = {
  up:    { y: 40, opacity: 0 },
  down:  { y: -40, opacity: 0 },
  left:  { x: -40, opacity: 0 },
  right: { x: 40, opacity: 0 },
  none:  { opacity: 0 },
}

export function mountRevealOnScroll(): () => void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))
  if (targets.length === 0) return () => {}

  // If reduced-motion, just show them all and return
  if (reduced) {
    targets.forEach((el) => {
      el.style.opacity = '1'
      el.style.transform = 'none'
    })
    return () => {}
  }

  // Pre-set initial state so no flash
  targets.forEach((el) => {
    const dir = ((el.dataset.reveal || 'up') as RevealDir) || 'up'
    gsap.set(el, DEFAULTS[dir])
  })

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target as HTMLElement
        io.unobserve(el)

        gsap.to(el, {
          x: 0,
          y: 0,
          opacity: 1,
          duration: 0.85,
          ease: 'power3.out',
          delay: Number(el.dataset.revealDelay || 0),
        })
      })
    },
    { root: null, threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
  )

  targets.forEach((el) => io.observe(el))

  return () => io.disconnect()
}
